import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { api } from '../api';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: 'month' | 'year';
  features: string[];
  limits: {
    pricingTables: number;
    apiCalls: number;
    analyticsRetention: number;
  };
}

interface Subscription {
  id: number;
  stripe_subscription_id: string;
  plan_name: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  plan: SubscriptionPlan;
}

export default function SubscriptionPage() {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchSubscriptionData();
  }, []);

  const fetchSubscriptionData = async () => {
    try {
      setLoading(true);
      const [plansResponse, subscriptionResponse] = await Promise.all([
        api.get('/subscription/plans'),
        api.get('/subscription/current')
      ]);

      setPlans(plansResponse.data.data);
      setCurrentSubscription(subscriptionResponse.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planId: string) => {
    try {
      setProcessing(true);
      const response = await api.post('/subscription/checkout', { planId });
      window.location.href = response.data.data.checkoutUrl;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create checkout session');
    } finally {
      setProcessing(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setProcessing(true);
      const response = await api.post('/subscription/portal');
      window.location.href = response.data.data.portalUrl;
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to access billing portal');
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your subscription? It will remain active until the end of the current billing period.')) {
      return;
    }

    try {
      setProcessing(true);
      await api.post('/subscription/cancel');
      await fetchSubscriptionData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to cancel subscription');
    } finally {
      setProcessing(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      setProcessing(true);
      await api.post('/subscription/reactivate');
      await fetchSubscriptionData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reactivate subscription');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Active</Badge>;
      case 'canceled':
        return <Badge className="bg-red-100 text-red-800">Canceled</Badge>;
      case 'past_due':
        return <Badge className="bg-yellow-100 text-yellow-800">Past Due</Badge>;
      case 'unpaid':
        return <Badge className="bg-red-100 text-red-800">Unpaid</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">Loading subscription data...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Subscription Management</h1>
        <p className="text-gray-600">Manage your subscription and billing</p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Current Subscription */}
      {currentSubscription && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Current Subscription</CardTitle>
            <CardDescription>
              {currentSubscription.plan.name} Plan - ${currentSubscription.plan.price}/{currentSubscription.plan.interval}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-600">Status</p>
                <div className="flex items-center gap-2">
                  {getStatusBadge(currentSubscription.status)}
                  {currentSubscription.cancel_at_period_end && (
                    <Badge className="bg-orange-100 text-orange-800">Canceling</Badge>
                  )}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Billing Period</p>
                <p className="font-medium">
                  {formatDate(currentSubscription.current_period_start)} - {formatDate(currentSubscription.current_period_end)}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={handleManageBilling} disabled={processing}>
                Manage Billing
              </Button>
              {currentSubscription.status === 'active' && !currentSubscription.cancel_at_period_end && (
                <Button variant="outline" onClick={handleCancelSubscription} disabled={processing}>
                  Cancel Subscription
                </Button>
              )}
              {currentSubscription.cancel_at_period_end && (
                <Button variant="outline" onClick={handleReactivateSubscription} disabled={processing}>
                  Reactivate Subscription
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Plans */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans.map((plan) => (
          <Card key={plan.id} className="relative">
            {currentSubscription?.plan_name === plan.id && (
              <div className="absolute top-4 right-4">
                <Badge className="bg-blue-100 text-blue-800">Current Plan</Badge>
              </div>
            )}
            <CardHeader>
              <CardTitle>{plan.name}</CardTitle>
              <CardDescription>
                <span className="text-2xl font-bold">${plan.price}</span>/{plan.interval}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center">
                    <svg className="w-4 h-4 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {feature}
                  </li>
                ))}
              </ul>

              {currentSubscription?.plan_name === plan.id ? (
                <Button disabled className="w-full">
                  Current Plan
                </Button>
              ) : (
                <Button 
                  onClick={() => handleSubscribe(plan.id)} 
                  disabled={processing}
                  className="w-full"
                >
                  {processing ? 'Processing...' : 'Subscribe'}
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
